import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtDateTime } from "../../lib/api";
import { toast } from "sonner";
import { RefreshCw, FileText, Film, Tv, Newspaper, ArrowUpRight } from "lucide-react";

export const AdminHome = () => {
  const [stats, setStats] = useState(null);
  const [articles, setArticles] = useState([]);
  const [syncing, setSyncing] = useState(false);

  const load = () => Promise.all([
    api.get("/releases/stats").then((r) => setStats(r.data)),
    api.get("/articles/all").then((r) => setArticles(r.data)),
  ]);

  useEffect(() => { load(); }, []);

  const runSync = async () => {
    setSyncing(true);
    const t = toast.loading("Synchronisation TMDB → Notion…");
    try {
      await api.post("/sync/run");
      toast.success("Synchronisation lancée en tâche de fond", {
        id: t,
        description: "Ça prend 2 à 5 min. Rafraîchis cette page ensuite pour voir le compteur.",
      });
      // Poll stats after 3 min to auto-refresh the dashboard
      setTimeout(() => load(), 180000);
    } catch (e) {
      toast.error("Sync échouée", { id: t, description: e?.message });
    } finally {
      setSyncing(false);
    }
  };

  const drafts = articles.filter((a) => a.status === "draft").length;
  const published = articles.filter((a) => a.status === "published").length;

  return (
    <div className="space-y-10">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-[var(--red)]">
            Rédaction
          </div>
          <h1 className="font-display text-4xl md:text-5xl mt-2">Tableau de bord</h1>
          <p className="text-neutral-600 mt-2 text-sm">
            {stats?.last_sync
              ? `Dernière synchro ${fmtDateTime(stats.last_sync.finished_at)} — ${stats.last_sync.status}`
              : "Aucune synchronisation encore lancée"}
          </p>
        </div>
        <div className="flex gap-3">
          <button
            data-testid="admin-sync-btn"
            onClick={runSync}
            disabled={syncing}
            className="btn-red"
          >
            <RefreshCw className={`h-4 w-4 ${syncing ? "animate-spin" : ""}`} />
            {syncing ? "Synchronisation…" : "Lancer la synchro"}
          </button>
          <Link to="/admin/articles/nouveau" data-testid="admin-new-article-btn" className="btn-ghost">
            <FileText className="h-4 w-4" /> Nouvel article
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Sorties TMDB" value={stats?.total ?? "—"} icon={Film} testId="stat-total" />
        <StatCard label="Films" value={stats?.films ?? "—"} icon={Film} testId="stat-films" />
        <StatCard label="Séries" value={stats?.series ?? "—"} icon={Tv} testId="stat-series" />
        <StatCard label="Articles" value={`${published}·${drafts}d`} hint="publiés · brouillons" icon={Newspaper} testId="stat-articles" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-[var(--line)] rounded-md p-6">
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-display text-xl">Articles récents</h2>
            <Link
              to="/admin/articles"
              className="text-xs uppercase tracking-[0.2em] text-[var(--red)]"
            >
              Tout voir
            </Link>
          </div>
          {articles.length === 0 ? (
            <div className="rounded-md border border-dashed border-[var(--line-strong)] py-10 text-center text-sm text-neutral-500">
              Aucun article pour l'instant. Écrivez le premier.
            </div>
          ) : (
            <div className="divide-y divide-[var(--line)]">
              {articles.slice(0, 6).map((a) => (
                <Link
                  key={a.id}
                  to={`/admin/articles/${a.id}`}
                  className="flex items-center justify-between py-3 group"
                >
                  <div className="min-w-0">
                    <div className="font-display text-lg truncate group-hover:text-[var(--red)]">
                      {a.title}
                    </div>
                    <div className="text-[11px] uppercase tracking-widest text-neutral-400 mt-0.5">
                      {a.status === "published" ? "Publié" : "Brouillon"} · {fmtDateTime(a.updated_at)}
                    </div>
                  </div>
                  <ArrowUpRight className="h-4 w-4 text-neutral-400 group-hover:text-[var(--red)]" />
                </Link>
              ))}
            </div>
          )}
        </div>
        <div className="bg-white border border-[var(--line)] rounded-md p-6">
          <h2 className="font-display text-xl mb-4">Automatisation</h2>
          <dl className="space-y-3 text-sm">
            <Row k="Fréquence" v="Quotidien" />
            <Row k="Heure" v="08:00 UTC" />
            <Row k="Fenêtre" v="60 jours" />
            <Row k="Région" v="France (FR)" />
            <Row
              k="Prochaine exécution"
              v={stats?.next_run_utc ? fmtDateTime(stats.next_run_utc) : "—"}
            />
          </dl>
          <Link
            to="/admin/reglages"
            className="mt-6 inline-flex items-center gap-1 text-xs text-[var(--red)]"
          >
            Configurer <ArrowUpRight className="h-3 w-3" />
          </Link>
        </div>
      </div>
    </div>
  );
};

const StatCard = ({ label, value, hint, icon: Icon, testId }) => (
  <div data-testid={testId} className="bg-white border border-[var(--line)] rounded-md p-5">
    <div className="flex items-start justify-between">
      <div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-400">{label}</div>
        <div className="font-display text-3xl md:text-4xl mt-2">{value}</div>
        {hint && <div className="text-xs text-neutral-500 mt-1">{hint}</div>}
      </div>
      <div className="h-8 w-8 grid place-items-center rounded-md bg-[var(--red-soft)] text-[var(--red)]">
        <Icon className="h-4 w-4" />
      </div>
    </div>
  </div>
);

const Row = ({ k, v }) => (
  <div className="flex justify-between border-b border-[var(--line)] pb-2 last:border-b-0 last:pb-0">
    <dt className="text-neutral-500">{k}</dt>
    <dd className="font-medium text-neutral-900">{v}</dd>
  </div>
);
